/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAppConfig, QUERY_PERSIST_CACHE } from '@airasia-rn/ui';
import { useAccessToken, useIsLoggedIn } from '@airasia-rn/account/hooks';
import { RewardsApiService } from '../services/rewardsApiService';
import { useHomeConfig } from '../config/HomeConfigProvider';
import { useNetworkStatus } from './useNetworkStatus';
import { useHomepageBlocks } from './useHomepage';

export const rewardsKeys = {
  profile: (currency: string) =>
    [QUERY_PERSIST_CACHE, 'rewards', 'profile', currency] as const,
};

export function useRewardsApiService(): RewardsApiService {
  const { config } = useHomeConfig();
  return useMemo(() => {
    return new RewardsApiService(config.homeRewardsApi);
  }, [config.homeRewardsApi]);
}

export const useRewardsProfile = (caller: string = 'rewards') => {
  const api = useRewardsApiService();
  const accessTokenQuery = useAccessToken(caller);
  const isLoggedInQuery = useIsLoggedIn();
  const appConfig = useAppConfig();
  const { responseCurrency } = useHomepageBlocks();
  const { isConnected } = useNetworkStatus();

  const currency = (
    appConfig.config.currency ||
    responseCurrency ||
    'USD'
  ).toUpperCase();
  const queryKey = useMemo(() => rewardsKeys.profile(currency), [currency]);

  const isEnabled = Boolean(
    isLoggedInQuery.isSuccess &&
      isLoggedInQuery.isLoggedIn &&
      accessTokenQuery.isTokenValid &&
      isConnected &&
      (appConfig.config.currency || responseCurrency),
  );

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      if (!accessTokenQuery.data) {
        throw new Error('Access token not available');
      }
      return await api.fetchMemberProfile(accessTokenQuery.data, currency);
    },

    staleTime: 10 * 1000,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    retry: 2,
    networkMode: 'offlineFirst',
    enabled: isEnabled,
  });

  return query;
};
